import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Layers, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COMMON = ["makeup", "hair", "nails", "lash", "culinary", "wellness", "events", "fitness", "music", "photography"];

export default function CreateDashboardWizardModal({ open, onOpenChange, onCreated }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [category, setCategory] = useState("");
  const [hostName, setHostName] = useState("");
  const [hostEmail, setHostEmail] = useState("");
  const [orgName, setOrgName] = useState("");
  const [creating, setCreating] = useState(false);

  const reset = () => { setStep(1); setCategory(""); setHostName(""); setHostEmail(""); setOrgName(""); };

  const create = async () => {
    if (!category || !hostName || !hostEmail) { toast.error("Category, host name and email required"); return; }
    setCreating(true);
    try {
      const r = await fetch(`${API}/admin/dashboards/create`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}`, "Content-Type": "application/json" },
        body: JSON.stringify({ category_id: category.toLowerCase(), host_email: hostEmail, host_name: hostName, org_name: orgName || hostName })
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(`Dashboard created · ${d.event_id}`);
        onCreated && onCreated(d);
        onOpenChange(false);
        reset();
        navigate(`/host/v2/${d.event_id}`);
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || "Create failed");
      }
    } catch { toast.error("Network error"); } finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Layers className="w-5 h-5 text-purple-600" /> Create Dashboard</DialogTitle></DialogHeader>
        {step === 1 && (
          <div className="space-y-3" data-testid="cdw-step-1">
            <p className="text-xs text-gray-500">Pick a category. The dashboard will pre-fill from the admin Category Master.</p>
            <div className="flex flex-wrap gap-1.5">
              {COMMON.map(c => (
                <button key={c} onClick={() => setCategory(c)} className={`text-xs px-3 py-1.5 rounded-full border ${category === c ? "bg-purple-600 text-white border-purple-600" : "bg-white text-gray-700 border-gray-200 hover:border-purple-300"}`} data-testid={`cdw-cat-${c}`}>{c}</button>
              ))}
            </div>
            <div>
              <p className="text-[10px] uppercase font-semibold text-gray-500 mb-1">Or type a new category id</p>
              <Input value={category} onChange={(e) => setCategory(e.target.value.toLowerCase())} placeholder="e.g. tattoo" data-testid="cdw-cat-input" />
            </div>
            <Button onClick={() => setStep(2)} disabled={!category} className="w-full" data-testid="cdw-next-1">Next <ArrowRight className="w-4 h-4 ml-1.5" /></Button>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-3" data-testid="cdw-step-2">
            <p className="text-xs text-gray-500">Host info. We'll create the user if it doesn't exist.</p>
            <Badge className="bg-purple-100 text-purple-700">{category}</Badge>
            <Input value={hostName} onChange={(e) => setHostName(e.target.value)} placeholder="Host name" data-testid="cdw-name-input" />
            <Input value={hostEmail} onChange={(e) => setHostEmail(e.target.value)} placeholder="host@example.com" type="email" data-testid="cdw-email-input" />
            <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="Org / brand name (optional)" data-testid="cdw-org-input" />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">Back</Button>
              <Button onClick={create} disabled={creating || !hostName || !hostEmail} className="flex-1 bg-purple-600 hover:bg-purple-700" data-testid="cdw-create-btn">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create dashboard"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
